<script lang="ts">
    // import Map from './Map.svelte';
    import { AuthManager } from "./helpers/AuthManager";
    import { bookCarAndRefresh, refreshBooking, cancelBooking } from "./helpers/BookingManager";
    import { isLoggedIn, bookingState, addLogEntry } from "./helpers/BookingState";
    import { getCoords } from "./helpers/Geocoder";
    import { get } from "svelte/store";
    // import { getVehicles } from "./helpers/Communauto";
    import {getVehicles} from "./helpers/Api";

    import Map from "./Map.svelte";
    import Listing from "./Listing.svelte";
    import { onMount, onDestroy } from "svelte";

    export let distance: number = 700;
    export let address: string;
    export let language: string;
    export let autoBookEnabled: boolean = false;
    export let closestCarEnabled: boolean = false;
    export let selectedModels: string[] = [];

    let effectiveDistance: number = distance;

    let firstLoad: boolean = true;
    let syncing: boolean = false;
    let center;
    let vehicles: any[] = [];
    let timerDuration = 8;
    let timer: number = timerDuration;
    let interval;
    let sound;
    let bookingStatusTimer; // For clearing the booking status message
    let bookingStatus = "";
    let isHandlingBooking = false;
    let syncRequestId = 0;

    $: {
        const goodVehicles = vehicles.filter(v => v.good);
        if (autoBookEnabled && goodVehicles.length > 0) {
            handleAutoBooking(goodVehicles[0]);
        }
    }
    // Use a function call so Svelte only tracks closestCarEnabled, not distance.
    // When the user unchecks the feature, reset to the original distance.
    const getDistance = () => distance;
    $: if (!closestCarEnabled) {
        effectiveDistance = getDistance();
    }

    $: {
        if (address && distance) {
            refreshMap();
        }
    }

    $: {
        if (selectedModels && center && !firstLoad) {
            syncVehicles();
        }
    }

    $: bookedCar = $bookingState?.booking ? {
        distance: "TODO",
        name: $bookingState.booking.CarNo,
        coords: {
            latitude: $bookingState.booking.Latitude,
            longitude: $bookingState.booking.Longitude
        },
        good: true,
        data: { ...$bookingState.booking, Id: $bookingState.booking.CarVin },
        energy: $bookingState.booking.EnergyLevel,
        id: $bookingState.booking.CarVin,
        booked: true
    } : null;

    async function refreshMap() {
        const id = ++syncRequestId;
        effectiveDistance = distance;
        firstLoad = true;
        center = await getCoords(address);
        const result = filterVehicles(await getVehicles(center, effectiveDistance, selectedModels));
        if (id !== syncRequestId) return;
        vehicles = result;
        firstLoad = false;
        timer = timerDuration;
    }

    onMount(() => {
        sound = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
        // Initial fetch is handled by the $: refreshMap() reactive.
        // Only set up the sync interval here.
        interval = setInterval(function () {
            if (timer > 0) timer--;
            if (timer == 0 && !syncing) syncVehicles();
        }, 1000);
    });

    onDestroy(() => {
        clearInterval(interval);
    });

    async function handleAutoBooking(vehicle) {
        if (isHandlingBooking) return;
        if (!isLoggedIn) return;
        if (vehicle.booked) return; // skip the already-booked car

        isHandlingBooking = true;
        try {
            // Refresh before checking to avoid acting on stale state
            if ($bookingState.booking) {
                await refreshBooking();
            }
            const carNo = vehicle.data.CarNo || vehicle.data.Name;
            addLogEntry(language == 'FR'
                ? `Voiture #${carNo} trouvée à ${vehicle.distance}m`
                : `Found car #${carNo} ${vehicle.distance}m away`);

            // After refresh, re-check (booking may have expired)
            if ($bookingState.booking) {
                if (!closestCarEnabled) return;
                // closestCarEnabled: cancel existing booking and rebook with closer car
                addLogEntry(language == 'FR'
                    ? `Annulation de la réservation pour la voiture #${$bookingState.booking.CarNo}...`
                    : `Canceling booking for car #${$bookingState.booking.CarNo}...`);
                await cancelBooking();
            }

            window.plausible('Auto Booking Attempt', {
                props: {
                    carId: vehicle.data.CarId,
                    distance: vehicle.distance,
                    model: vehicle.data.ModelName
                }
            });

            const resp = await bookCarAndRefresh(vehicle.data.CarId);
            if (!resp.success) {
                bookingStatus = resp.message;
                window.plausible('Auto Booking Failed', {
                    props: {
                        carId: vehicle.data.CarId,
                        reason: resp.message
                    }
                });
            } else {
                bookingStatus = language == 'FR' ? 'Réservation réussie' : 'Booking successful';
                addLogEntry(language == 'FR'
                    ? `Réservation réussie pour la voiture #${carNo}`
                    : `Booking successful for car #${carNo}`);

                if (closestCarEnabled && typeof vehicle.distance === 'number') {
                    effectiveDistance = Math.max(50, Math.round(vehicle.distance * 0.9));
                    addLogEntry(language == 'FR'
                        ? `Rayon ajusté à ${effectiveDistance}m`
                        : `Adjusting distance to ${effectiveDistance}m`);
                }

                if (sound) {
                    sound.play().catch(err => console.error('Error playing sound:', err));
                }
                if (bookingStatusTimer) clearTimeout(bookingStatusTimer);
                bookingStatusTimer = setTimeout(() => {
                    bookingStatus = "";
                }, 5000);

                window.plausible('Auto Booking Success', {
                    props: {
                        carId: vehicle.data.CarId,
                        model: vehicle.data.ModelName,
                        distance: vehicle.distance
                    }
                });
            }
        } finally {
            isHandlingBooking = false;
        }
    }


    function filterVehicles(vehicles) {
        if (vehicles.some(v => v.good)) {
            // displayNotification();
            if (sound) {
                sound.play();
            } else {
                setTimeout(function () {
                    if (sound) {
                        sound.play();
                    }
                }, 100);
            }
        }
        let filteredVehicles = vehicles
            .filter(function (vehicle) {
                return vehicle.good || vehicle.distance <= distance * 1.2;
            })
            .sort(function (a, b): number {
                return a.distance - b.distance;
            });


        // Add the vehicle from the booking to the list
        if ($bookingState.booking) {
            const booking = $bookingState?.booking;

            const bookedCar = {
                distance: "TODO",
                name: booking.CarNo,
                coords: {
                    latitude: booking.Latitude,
                    longitude: booking.Longitude
                },
                good: true,
                data: { ...booking, Id: booking.CarVin },
                energy: null,
                id: booking.CarVin,
                booked: true
            };
            filteredVehicles = [...filteredVehicles, bookedCar];
        }
        return filteredVehicles;
    }

    async function syncVehiclesButton() {
        window.plausible('Manual Sync');
        syncVehicles();
    }

    async function syncVehicles() {
        const id = ++syncRequestId;
        syncing = true;
        const result = filterVehicles(await getVehicles(center, effectiveDistance, selectedModels));
        if (id !== syncRequestId) return;
        vehicles = result;
        syncing = false;
        timer = timerDuration;
    }

    function displayNotification() {
        if (Notification.permission == "granted") {
            navigator.serviceWorker.getRegistration().then(function (reg) {
                reg.showNotification("A car is available");
            });
        }
    }
</script>

<div class="h-full flex flex-col">
  {#if firstLoad}
    <div class="p-4">
      <p>{language == 'FR' ? 'Chargement...' : 'Loading...'}</p>
    </div>
  {:else}
    <!-- Map Controls Overlay -->
    <div class="bg-white p-4 border-b">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <label class="text-sm text-gray-600 block">
            {language == 'FR' ? 'Prochaine synchronisation dans : ' : 'Next sync in : '}
            {timer}s
          </label>
          <progress class="w-full h-2 bg-gray-200" max={timerDuration} value={timer} />
          {#if closestCarEnabled && effectiveDistance !== distance}
            <p class="text-xs text-gray-500 mt-1">{language == 'FR' ? 'Rayon actif' : 'Active radius'} : {effectiveDistance}m</p>
          {/if}
        </div>

        <button
          on:click={syncVehiclesButton}
          class="ml-4 bg-commugreen hover:bg-commugreen-dark text-white px-4 py-2 rounded transition-colors"
        >
          {language == 'FR' ? 'Synchroniser' : 'Sync now'}
        </button>
      </div>

    </div>

    <!-- Map Container -->
    <div class="flex-1 relative">
      <Map {language} {center} vehicles={vehicles} bookedCar={bookedCar} distance={effectiveDistance} />
    </div>

    {#if bookingStatus}
      <div class="absolute bottom-4 left-4 right-4 p-4 rounded shadow-lg"
        class:bg-green-100={bookingStatus.includes('success')}
        class:bg-red-100={!bookingStatus.includes('success')}>
        {bookingStatus}
      </div>
    {/if}
  {/if}
</div>
